import type { EraDef, VisualDef } from '../types';

/**
 * Retained scaffold helper for future eras. All ten campaign eras are now
 * fully authored, so ERA_STUBS intentionally remains empty.
 */

export interface StubParams {
  index: number;
  id: string;
  code: string; // unique per-era id prefix, e.g. 'i4_'
  name: string;
  subtitle: string;
  palette: EraDef['palette'];
  environmentKind: string;
  groundStyle: EraDef['environment']['groundStyle'];
  props: EraDef['environment']['props'];
  swarmerName: string;
  swarmerVisual: VisualDef;
  shooterName: string;
  shooterVisual: VisualDef;
  bossName: string;
  bossTitle: string;
  bossVisual: VisualDef;
}

export function makeEraStub(p: StubParams): EraDef {
  const c = p.code;
  const enemyBullet = p.palette.enemyBullet;
  const swarmerId = `${c}swarmer`;
  const shooterId = `${c}shooter`;
  const aimedId = `${c}aimed3`;
  const radialId = `${c}radial8`;
  const bossId = `${c}boss`;

  return {
    index: p.index,
    id: p.id,
    name: p.name,
    subtitle: p.subtitle,
    implemented: false,
    palette: p.palette,
    environment: {
      kind: p.environmentKind,
      arenaRadius: 60,
      groundStyle: p.groundStyle,
      props: p.props,
    },
    enemies: [
      {
        id: swarmerId,
        name: p.swarmerName,
        archetype: 'swarmer',
        hp: 14,
        speed: 4,
        damage: 8,
        radius: 0.4,
        xp: 5,
        visual: p.swarmerVisual,
        behavior: { type: 'chase' },
      },
      {
        id: shooterId,
        name: p.shooterName,
        archetype: 'shooter',
        hp: 36,
        speed: 2.2,
        damage: 10,
        radius: 0.55,
        xp: 8,
        visual: p.shooterVisual,
        behavior: { type: 'keepDistance', distance: 10, strafe: 0.3 },
        patterns: [aimedId],
        patternCooldown: 1.8,
        fireRange: 16,
      },
    ],
    patterns: [
      {
        id: aimedId,
        emitters: [{ shape: 'aimed', interval: 0.12, bursts: 3, count: 1, bullet: { speed: 7, lifetime: 3, radius: 0.22, damage: 8, visual: 'orb', color: enemyBullet } }],
      },
      {
        id: radialId,
        emitters: [{ shape: 'radial', interval: 1, bursts: 1, count: 8, bullet: { speed: 4.5, lifetime: 4, radius: 0.25, damage: 9, visual: 'orb', color: enemyBullet } }],
      },
    ],
    boss: {
      id: bossId,
      name: p.bossName,
      title: p.bossTitle,
      hp: 1800,
      radius: 2.6,
      speed: 1.5,
      damage: 24,
      xp: 200,
      visual: p.bossVisual,
      phases: [
        { hpBelow: 1, name: 'Awaken', behavior: { type: 'keepDistance', distance: 10, strafe: 0.25 }, patterns: [radialId], patternCooldown: 1.6, transition: 1.5 },
        { hpBelow: 0.5, name: 'Fury', behavior: { type: 'chase' }, patterns: [radialId], patternCooldown: 1.1, transition: 2, speedMult: 1.4 },
      ],
    },
    stages: [
      {
        name: `${p.name} I`,
        duration: 180,
        density: { start: 1, end: 2.4 },
        pool: [
          { enemy: swarmerId, weight: 10 },
          { enemy: shooterId, weight: 4, minTime: 25 },
        ],
        waves: [
          { t: 3, enemy: swarmerId, count: 8, formation: 'ring' },
          { t: 60, enemy: shooterId, count: 4, formation: 'line' },
        ],
      },
      {
        name: `${p.name} II`,
        duration: 200,
        density: { start: 1.6, end: 3.4 },
        pool: [
          { enemy: swarmerId, weight: 8 },
          { enemy: shooterId, weight: 5 },
        ],
        waves: [
          { t: 5, enemy: shooterId, count: 5, formation: 'ring' },
          { t: 90, enemy: swarmerId, count: 16, formation: 'cluster' },
        ],
      },
      {
        name: p.bossName,
        duration: 600,
        density: { start: 1.2, end: 1.2 },
        pool: [
          { enemy: swarmerId, weight: 6 },
          { enemy: shooterId, weight: 3 },
        ],
        waves: [{ t: 2, enemy: swarmerId, count: 10, formation: 'ring' }],
        boss: { id: bossId, at: 15 },
      },
    ],
    loot: [],
    audio: { music: `music/era${p.index + 1}_${p.id}`, ambient: `amb/${p.id}` },
  } as EraDef;
}


export const ERA_STUBS: EraDef[] = [];
